/**
 * CRF panel (D21 lean): gate summary, read-only statement grid, C1 verification,
 * cumulative feedback editor, overridable verdict, finalize / PDF / email.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, Switch, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { InfoHint } from '@/components/tutorial/InfoHint';
import type {
  CompetencyReviewForm,
  CrfCumulativeFeedback,
  CrfVerdict,
} from '@/types/mentorEngagements';
import { ActionButton, Chip, Panel, TextField, mentorStyles } from './shared';
import { CrfStatementGrid } from './CrfStatementGrid';
import { CumulativeFeedbackEditor } from './CumulativeFeedbackEditor';
import { liveComputedVerdict } from './crfVerdict';

interface CrfPanelProps {
  crf: CompetencyReviewForm | null;
  loading: boolean;
  readOnly?: boolean;
  hasSessions: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  onSave: (payload: {
    c1_verified: boolean | null;
    c1_notes: string;
    cumulative_feedback: CrfCumulativeFeedback | null;
  }) => Promise<CompetencyReviewForm>;
  /** Per-statement override; PUT …/crf `overrides.statements` and returns the recomputed form. */
  onToggleStatement?: (statementId: string, next: boolean) => Promise<CompetencyReviewForm>;
  onGenerateNarrative: () => void;
  onFinalize: (verdict: CrfVerdict) => void;
  onReopen: () => void;
  onDownloadPdf: () => void;
  onEmail: () => void;
}

export function CrfPanel({
  crf,
  readOnly: accessReadOnly = false,
  loading,
  hasSessions,
  loadError,
  onRetry,
  onSave,
  onToggleStatement,
  onGenerateNarrative,
  onFinalize,
  onReopen,
  onDownloadPdf,
  onEmail,
}: CrfPanelProps) {
  const { t } = useTranslation();
  const [c1Verified, setC1Verified] = useState(false);
  const [c1Notes, setC1Notes] = useState('');
  const [feedback, setFeedback] = useState<CrfCumulativeFeedback | null>(null);
  const [verdictOverride, setVerdictOverride] = useState<CrfVerdict | null>(null);
  const [saving, setSaving] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const dirty = useRef(false);
  const identity = useRef<string | null>(null);

  useEffect(() => {
    if (!crf) return;
    const nextIdentity = `${crf.id}:${crf.status}`;
    if (identity.current !== nextIdentity) {
      identity.current = nextIdentity;
      dirty.current = false;
      setVerdictOverride(null);
    }
    if (dirty.current) return;
    setC1Verified(crf.overrides?.c1_verified === true);
    setC1Notes(crf.overrides?.c1_notes || '');
    setFeedback(crf.cumulative_feedback || crf.narrative_draft
      ? {
        strengths: crf.cumulative_feedback?.strengths || crf.narrative_draft?.strengths || [],
        development: crf.cumulative_feedback?.development || crf.narrative_draft?.development || [],
      }
      : null);
  }, [crf]);

  const readOnly = accessReadOnly || crf?.status === 'final';
  const computedVerdict = liveComputedVerdict(crf?.computed, c1Verified);
  const effectiveVerdict = verdictOverride ?? crf?.overall_verdict ?? computedVerdict;
  const overrideMismatch = effectiveVerdict !== computedVerdict;

  // Finalize freezes server state, so unsaved C1/feedback edits must land first.
  const finalizeWithSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const saved = await onSave({ c1_verified: c1Verified, c1_notes: c1Notes, cumulative_feedback: feedback });
      dirty.current = false;
      onFinalize(verdictOverride ?? saved.overall_verdict ?? saved.computed.verdict);
    } catch {
      setMessage(t('mentorEngagements.crf.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave({ c1_verified: c1Verified, c1_notes: c1Notes, cumulative_feedback: feedback });
      dirty.current = false;
      setMessage(t('mentorEngagements.crf.saved'));
    } catch {
      setMessage(t('mentorEngagements.crf.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Overrides save immediately: the server recomputes gates, and staging them
  // locally could not update the gate chips.
  const toggleStatement = async (statementId: string, next: boolean) => {
    if (!onToggleStatement || overrideBusy) return;
    setOverrideBusy(true);
    setOverrideMessage(null);
    try {
      await onToggleStatement(statementId, next);
      setOverrideMessage(t('mentorEngagements.overrides.saved'));
    } catch {
      setOverrideMessage(t('mentorEngagements.overrides.error'));
    } finally {
      setOverrideBusy(false);
    }
  };

  const deliverReviewedForm = async (deliver: () => void) => {
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      if (!readOnly) {
        await onSave({ c1_verified: c1Verified, c1_notes: c1Notes, cumulative_feedback: feedback });
        dirty.current = false;
      }
      deliver();
    } catch {
      setMessage(t('mentorEngagements.crf.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Panel testID="crf_panel">
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.crf.title')}</Text>
        <View style={mentorStyles.center}><ActivityIndicator color={reviewDesk.accent} /></View>
      </Panel>
    );
  }

  if (loadError || (!crf && hasSessions)) {
    return <Panel testID="crf_load_error">
      <Text accessibilityRole="alert" style={mentorStyles.errorText}>{loadError || t('mentorEngagements.detail.loadError')}</Text>
      <View style={mentorStyles.row}>
        <ActionButton testID="crf_retry" icon="refresh" label={t('mentorEngagements.retry')} onPress={() => onRetry?.()} />
      </View>
    </Panel>;
  }

  if (!crf || !hasSessions) {
    return (
      <Panel testID="crf_panel">
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.crf.title')}</Text>
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.crf.noSessions')}</Text>
      </Panel>
    );
  }

  return (
    <Panel testID="crf_panel">
      <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
        <View style={{ flexShrink: 1 }}>
          <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.crf.title')}</Text>
          <Text style={mentorStyles.panelSubtitle}>{t('mentorEngagements.crf.subtitle')}</Text>
          <InfoHint id="crf_form" style={{ marginTop: 6 }} />
        </View>
        <View style={mentorStyles.row}>
          <Chip label={crf.level.toUpperCase()} tone="accent" />
          <Chip
            label={t(`mentorEngagements.formStatus.${crf.status}`)}
            tone={crf.status === 'final' ? 'success' : 'neutral'}
          />
          <Chip label={`rev. ${crf.form_revision}`} />
        </View>
      </View>

      {readOnly ? (
        <View style={[mentorStyles.row, { gap: 6 }]}>
          <MaterialIcons name="lock" size={16} color={reviewDesk.muted} />
          <Text style={mentorStyles.mutedText}>{t('mentorEngagements.crf.locked')}</Text>
        </View>
      ) : null}

      <CrfStatementGrid
        level={crf.level}
        computed={crf.computed}
        onToggle={!readOnly && onToggleStatement ? toggleStatement : undefined}
        busy={overrideBusy}
      />
      {overrideMessage ? (
        <Text testID="crf_override_message" accessibilityLiveRegion="polite" style={mentorStyles.mutedText}>{overrideMessage}</Text>
      ) : null}

      {/* C1 verification */}
      <View style={{ gap: 8, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}>
        <Text style={[mentorStyles.panelTitle, { fontSize: 15 }]}>{t('mentorEngagements.crf.c1Title')}</Text>
        <View style={mentorStyles.row}>
          <Switch
            testID="crf_c1_verified"
            value={c1Verified}
            onValueChange={(value) => { dirty.current = true; setC1Verified(value); }}
            accessibilityLabel={t('mentorEngagements.crf.c1Verified')}
            disabled={readOnly || saving}
            trackColor={{ false: reviewDesk.line, true: reviewDesk.accentSoft }}
            thumbColor={c1Verified ? reviewDesk.accent : reviewDesk.muted}
          />
          <Text style={[mentorStyles.bodyText, mentorStyles.flexOne]}>
            {t('mentorEngagements.crf.c1Verified')}
          </Text>
        </View>
        <TextField
          label={t('mentorEngagements.crf.c1Notes')}
          value={c1Notes}
          onChangeText={(value) => { dirty.current = true; setC1Notes(value); }}
          multiline
          editable={!readOnly && !saving}
        />
      </View>

      <View style={{ borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}>
        <CumulativeFeedbackEditor
          feedback={feedback}
          hasDraft={Boolean(crf.narrative_draft)}
          generating={Boolean(crf.narrative_pending)}
          readOnly={readOnly || saving}
          onChange={(value) => { dirty.current = true; setFeedback(value); }}
          onGenerate={onGenerateNarrative}
        />
        {!readOnly && crf.narrative_draft && !crf.narrative_pending ? (
          <ActionButton label={t('mentorEngagements.crf.useDraft')} disabled={saving}
            onPress={() => { dirty.current = true; setFeedback({ strengths: crf.narrative_draft?.strengths || [], development: crf.narrative_draft?.development || [] }); }} />
        ) : null}
      </View>

      {/* Verdict */}
      <View style={{ gap: 8, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}>
        <Text style={[mentorStyles.panelTitle, { fontSize: 15 }]}>{t('mentorEngagements.crf.verdict')}</Text>
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.crf.verdictComputed')}</Text>
        {(['demonstrated', 'not_yet'] as CrfVerdict[]).map((verdict) => {
          const selected = effectiveVerdict === verdict;
          return (
            <Pressable
              key={verdict}
              testID={`crf_verdict_${verdict}`}
              onPress={() => !readOnly && setVerdictOverride(verdict)}
              disabled={readOnly}
              accessibilityRole="radio"
              accessibilityState={{ selected, disabled: readOnly }}
              aria-selected={selected}
              style={({ pressed }) => [mentorStyles.row, pressed && mentorStyles.pressed]}
            >
              <MaterialIcons
                name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                size={19}
                color={selected ? reviewDesk.accent : reviewDesk.muted}
              />
              <Text style={[mentorStyles.bodyText, mentorStyles.flexOne]}>
                {verdict === 'demonstrated'
                  ? t('mentorEngagements.crf.verdictDemonstrated')
                  : t('mentorEngagements.crf.verdictNotYet')}
              </Text>
            </Pressable>
          );
        })}
        {overrideMismatch ? (
          <View style={{ flexDirection: 'row', gap: 8, padding: 10, borderRadius: 12, backgroundColor: reviewDesk.amberSoft }}>
            <MaterialIcons name="warning-amber" size={17} color={reviewDesk.amber} />
            <Text style={[mentorStyles.bodyText, mentorStyles.flexOne]}>
              {t('mentorEngagements.crf.verdictOverrideWarning')}
            </Text>
          </View>
        ) : null}
      </View>

      <View style={[mentorStyles.row, { borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }]}>
        {!readOnly ? (
          <ActionButton
            testID="crf_save"
            label={t('mentorEngagements.crf.save')}
            icon="save"
            variant="primary"
            onPress={save}
            disabled={saving}
          />
        ) : null}
        <ActionButton
          testID="crf_download_pdf"
          label={t('mentorEngagements.crf.downloadPdf')}
          icon="picture-as-pdf"
          onPress={() => deliverReviewedForm(onDownloadPdf)}
          disabled={saving}
        />
        <ActionButton
          testID="crf_email"
          label={t('mentorEngagements.crf.emailToMentee')}
          icon="mail-outline"
          onPress={() => deliverReviewedForm(onEmail)}
          disabled={saving || accessReadOnly}
        />
        {readOnly ? (
          <ActionButton
            testID="crf_reopen"
            label={t('mentorEngagements.crf.reopen')}
            icon="lock-open"
            onPress={onReopen}
            disabled={accessReadOnly}
          />
        ) : (
          <ActionButton
            testID="crf_finalize"
            label={t('mentorEngagements.crf.finalize')}
            icon="verified"
            onPress={finalizeWithSave}
            disabled={saving || Boolean(crf.narrative_pending)}
          />
        )}
        {message ? <Text style={mentorStyles.mutedText}>{message}</Text> : null}
      </View>
    </Panel>
  );
}

export default CrfPanel;
